import { useEffect, useState } from 'react';
import Modal from '@v2/shared/components/Modal';
import CancelSurveyForm from './CancelSurveyForm';
import DelayAutoRenewalForm from './DelayAutoRenewalForm';
import RetentionOfferForm from './RetentionOfferForm';
import SuccessStep from './SuccessStep';
import { delaySuccessMessage, disableSuccessMessage, offerAppliedMessage } from './messages';
import { useAutoRenewalAction } from './useAutoRenewal';
import { AutoRenewalDetail, CancelReason, Props } from './types';

type FlowStep = 'survey' | 'retention-offer' | 'delay' | 'success';

type ModalProps = Pick<Props, 'id' | 'loginUrl' | 'autoRenewServiceName'> & {
  isOpen: boolean;
  close: () => void;
  autoRenewal: AutoRenewalDetail;
  onUpdated: (autoRenewal: AutoRenewalDetail) => void;
};

/**
 * The offer a customer is shown on their way out, when there is a coherent one to make.
 *
 * Both prices have to be there, the original has to be more than nothing, and the
 * discount has to actually be a discount. That is stricter than headless_jshop, which
 * entered the flow on the mere presence of a `retention_offer` and then divided by
 * whatever it found: an unpriced upcoming transaction gave "an additional NaN% off", and
 * a comped one "-Infinity% off". An offer that cannot be stated is not an offer, so the
 * flow opens on the delay form instead — which is what it already does for the far more
 * common case of no offer at all.
 */
function retentionOffer(autoRenewal: AutoRenewalDetail) {
  const upcoming = autoRenewal.upcoming_transaction;
  const discountedPrice = upcoming?.retention_offer?.discounted_price;

  if (!upcoming || upcoming.price == null || upcoming.price <= 0 || discountedPrice == null) {
    return null;
  }

  return discountedPrice < upcoming.price
    ? { originalPrice: upcoming.price, discountedPrice }
    : null;
}

/**
 * The flow between a customer clicking "Disable" and the subscription actually stopping.
 *
 * Three ways through it, and which one a customer gets is decided by what the api has
 * already put on the subscription. With a retention offer waiting on the next
 * transaction: ask why, and answer the reason — "renewing too soon" is a problem a delay
 * solves, so that one goes to the delay form and every other reason to the offer. With no
 * offer to make, there is nothing to ask about, so the flow opens on the delay form
 * directly.
 *
 * Every step carries the same way out: CancelWarningBox, which disables regardless. That
 * is deliberate — a customer who came here to cancel can, on the first screen, without
 * reading a word of it. Which is also why every step has to be able to show the disable
 * request's error: the button is on all three, so a failure on any of them has somewhere
 * to be said.
 *
 * What the disable request carries is the reason, when one was given, and whether an offer
 * was put in front of them. That second flag is why `offerShown` is tracked here rather
 * than inferred: by the time they disable, the step they saw it on is gone.
 */
export default function DisableAutoRenewalModal({
  isOpen,
  close,
  id,
  loginUrl,
  autoRenewal,
  autoRenewServiceName,
  onUpdated,
}: ModalProps) {
  const offer = retentionOffer(autoRenewal);
  const initialStep: FlowStep = offer ? 'survey' : 'delay';

  const [currentStep, setCurrentStep] = useState<FlowStep>(initialStep);
  const [selectedReason, setSelectedReason] = useState<CancelReason | null>(null);
  const [offerShown, setOfferShown] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const succeed = (message: string) => {
    setSuccessMessage(message);
    setCurrentStep('success');
  };

  const disable = useAutoRenewalAction({
    id,
    loginUrl,
    action: 'disable',
    errorMessage: 'Oops, something went wrong. Try again later.',
    onSuccess: (updated) => {
      succeed(disableSuccessMessage(autoRenewServiceName));
      onUpdated(updated);
    },
  });

  const applyOffer = useAutoRenewalAction({
    id,
    loginUrl,
    action: 'apply_offer',
    errorMessage: 'There was an error applying your special offer discount, please try again.',
    onSuccess: (updated) => {
      succeed(offerAppliedMessage(autoRenewServiceName, updated.upcoming_transaction?.price));
      onUpdated(updated);
    },
  });

  const delay = useAutoRenewalAction({
    id,
    loginUrl,
    action: 'delay',
    errorMessage: 'Oops, something went wrong. Try again later.',
    onSuccess: (updated) => {
      succeed(delaySuccessMessage(autoRenewServiceName, updated));
      onUpdated(updated);
    },
  });

  const runDisable = () => disable.run({ reason: selectedReason ?? undefined, offer_shown: offerShown });

  const goBack = () => {
    if (offer) {
      setCurrentStep('survey');
    }

    setSelectedReason(null);
  };

  useEffect(() => {
    if (currentStep === 'retention-offer') {
      setOfferShown(true);
    }
  }, [currentStep]);

  // Closed and reopened is a fresh flow, not the tail of the last one.
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(initialStep);
      setSelectedReason(null);
      setOfferShown(false);
      setSuccessMessage('');
      disable.reset();
      applyOffer.reset();
      delay.reset();
    }
    // The reset functions are stable for the life of their hooks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialStep]);

  const titles: Record<FlowStep, string> = {
    survey: `Disable ${autoRenewServiceName}`,
    'retention-offer': 'We Can Fix That!',
    delay: offer ? `Delay ${autoRenewServiceName}` : 'Take a Break Instead',
    success: 'Action Complete',
  };

  return (
    <Modal isOpen={isOpen} close={close} title={titles[currentStep]}>
      {currentStep === 'survey' && (
        <CancelSurveyForm
          autoRenewServiceName={autoRenewServiceName}
          onContinueWithSurvey={(reason) => {
            setSelectedReason(reason);
            setCurrentStep(reason === 'renewing_too_soon' ? 'delay' : 'retention-offer');
          }}
          onDisable={runDisable}
          loading={disable.loading}
          error={disable.error}
        />
      )}

      {currentStep === 'retention-offer' && offer && (
        <RetentionOfferForm
          autoRenewServiceName={autoRenewServiceName}
          originalPrice={offer.originalPrice}
          discountedPrice={offer.discountedPrice}
          // The reason is always set here: this step is only ever reached through the
          // survey, which will not submit without one.
          onAcceptOffer={(discountedPrice) =>
            applyOffer.run({ discounted_price: discountedPrice, reason: selectedReason })
          }
          onDisable={runDisable}
          onGoBack={goBack}
          loading={disable.loading}
          acceptOfferLoading={applyOffer.loading}
          error={applyOffer.error ?? disable.error}
        />
      )}

      {currentStep === 'delay' && (
        <DelayAutoRenewalForm
          autoRenewal={autoRenewal}
          autoRenewServiceName={autoRenewServiceName}
          onDelay={(months) => delay.run({ months })}
          loading={delay.loading}
          error={delay.error ?? disable.error}
          showDisableFunctionality
          onContinueToDisable={runDisable}
          onGoBack={offer ? goBack : undefined}
          disableLoading={disable.loading}
        />
      )}

      {currentStep === 'success' && <SuccessStep message={successMessage} onClose={close} />}
    </Modal>
  );
}
